import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    ReloadOutlined,
    UnorderedListOutlined,
} from "@ant-design/icons";
import {
    Button,
    Card,
    Table,
    Tag,
    message,
    Modal,
    Input,
    Space,
    Tooltip,
} from "antd";
import { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import dayjs from "dayjs";

const { TextArea } = Input;

export default function StatusUpdation() {
    const { authData, backUrl } = useContext(AuthContext);
    const isMobile = window.innerWidth < 768;

    // State management
    const [dataSource, setDataSource] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Modal state for approval/rejection
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [actionType, setActionType] = useState(""); // "approve" or "reject"
    const [remarks, setRemarks] = useState("");
    const [submitting, setSubmitting] = useState(false);

    // Status color mapping
    const getStatusColor = (status) => {
        const statusColors = {
            "PRESENT": "green",
            "PERMISSION": "orange",
            "ABSENT": "red",
            "HOLIDAY": "blue",
            "Weekoff": "purple",
            "Compoff": "cyan",
            "CASUAL LEAVE": "magenta",
            "SICK LEAVE": "volcano",
            "HALFDAY_LEAVE": "geekblue",
        };
        return statusColors[status] || "default";
    };

    // Request status color mapping
    const getRequestStatusColor = (status) => {
        const statusColors = {
            "pending": "gold",
            "approved": "green",
            "rejected": "red",
        };
        return statusColors[status?.toLowerCase()] || "default";
    };

    // Fetch pending status change requests
    const fetchPendingRequests = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.append('page', currentPage);
            params.append('page_size', pageSize);

            const url = `${backUrl}/api/hr/attendance-changes/pending-list/?${params.toString()}`;
            console.log("Fetching pending requests from:", url);

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            console.log("Backend Response:", res.data);

            const data = res.data.results || res.data;
            setDataSource(data);
            setTotalRecords(res.data.count || data.length);
        } catch (error) {
            console.error("Error fetching pending requests:", error);
            message.error("Failed to fetch pending status change requests");
            setDataSource([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        fetchPendingRequests();
    }, [currentPage, pageSize]);

    // Handle table pagination change
    const handleTableChange = (pagination) => {
        setCurrentPage(pagination.current);
        setPageSize(pagination.pageSize);
    };

    // Open approval/rejection modal
    const handleActionClick = (record, action) => {
        setSelectedRequest(record);
        setActionType(action);
        setRemarks("");
        setIsModalVisible(true);
    };

    // Submit approval or rejection
    const handleSubmitAction = async () => {
        if (!selectedRequest) return;

        // Require reason for both approve and reject
        if (!remarks.trim()) {
            message.warning(`Please enter a reason for ${actionType === "approve" ? "approval" : "rejection"}`);
            return;
        }

        setSubmitting(true);
        try {
            const endpoint = actionType === "approve"
                ? `${backUrl}/api/hr/attendance-changes/${selectedRequest.id}/approve/`
                : `${backUrl}/api/hr/attendance-changes/${selectedRequest.id}/reject/`;

            const payload = {
                reason: remarks.trim()
            };

            await axios.post(endpoint, payload, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });

            message.success(
                `Status change request ${actionType === "approve" ? "approved" : "rejected"} successfully!`
            );

            setIsModalVisible(false);
            setRemarks("");
            setSelectedRequest(null);
            setActionType("");
            fetchPendingRequests();
        } catch (error) {
            console.error(`Error ${actionType}ing request:`, error);

            const errorMessage =
                error.response?.data?.detail ||
                error.response?.data?.message ||
                error.response?.data?.error ||
                `Failed to ${actionType} status change request`;

            message.error(errorMessage);
        } finally {
            setSubmitting(false);
        }
    };

    // Handle modal cancel
    const handleModalCancel = () => {
        setIsModalVisible(false);
        setRemarks("");
        setSelectedRequest(null);
        setActionType("");
    };

    // Table columns definition
    const columns = [
        {
            title: 'Requested By',
            dataIndex: "requested_by_name",
            key: 'requested_by'
        },
        {
            title: "Employee Code",
            dataIndex: "emp_code",
            key: "emp_code",
            width: 180,
        },
        {
            title: "Employee Name",
            dataIndex: "employee_name",
            key: "employee_name",
            width: 180,
        },
        {
            title: "Department",
            dataIndex: "department_name",
            key: "department",
            width: 150,
        },
        {
            title: "Designation",
            dataIndex: "designation_name",
            key: "designation",
            width: 150,
        },
        {
            title: "Attendance Date",
            dataIndex: "date",
            key: "date",
            width: 130,
        },
        {
            title: "Current Status",
            dataIndex: "status",
            key: "current_status",
            width: 130,
        },
        {
            title: "Requested Status",
            dataIndex: "pending_status_change",
            key: "new_status",
        },
        {
            title: "Remarks",
            dataIndex: "status_change_remarks",
            key: "remarks",
            width: 200,
        },
        {
            title: "Action",
            key: "action",
            width: 180,
            render: (_, record) => {
                const isPending = !record.status || record.status.toLowerCase() === "pending";

                return (
                    <Space size="small">
                        <Button
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            onClick={() => handleActionClick(record, "approve")}
                            style={{
                                backgroundColor: "#52c41a",
                                borderColor: "#52c41a",
                            }}
                            shape="round"
                        >
                        </Button>
                        <Button
                            danger
                            icon={<CloseCircleOutlined />}
                            onClick={() => handleActionClick(record, "reject")}
                            shape="round"
                        >
                        </Button>
                    </Space>
                )
            },
        },
    ];

    return (
        <section>
            <center>
                {isMobile ? (
                    <p style={{ fontSize: "15px", textTransform: "uppercase", color: "#0e497b" }}>
                        Status Change Requests
                    </p>
                ) : (
                    <h5>
                        Attendance Status Change Requests
                    </h5>
                )}
            </center>

            {/* Requests Table */}
            <div style={{ margin: "20px" }}>
                <Table
                    columns={columns}
                    dataSource={dataSource}
                    bordered
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: pageSize,
                        total: totalRecords,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
                    }}
                    scroll={{ x: "max-content" }}
                    rowKey={(record) => record.id}
                    onChange={handleTableChange}
                    components={{
                        header: {
                            cell: (props) => (
                                <th
                                    {...props}
                                    style={{
                                        backgroundColor: "#0e497b",
                                        color: "white",
                                    }}
                                >
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                />
            </div>

            {/* Approval/Rejection Modal */}
            <Modal
                title={actionType === "approve" ? "Approve Status Change" : "Reject Status Change"}
                open={isModalVisible}
                onOk={handleSubmitAction}
                onCancel={handleModalCancel}
                confirmLoading={submitting}
                okText={actionType === "approve" ? "Approve" : "Reject"}
                okButtonProps={{
                    danger: actionType === "reject",
                    style: actionType === "approve"
                        ? { backgroundColor: "#52c41a", borderColor: "#52c41a" }
                        : undefined,
                }}
                cancelText="Cancel"
                width={600}
            >
                {selectedRequest && (
                    <div>
                        <div style={{ marginBottom: "20px" }}>
                            <p>
                                <strong>Employee:</strong>{" "}
                                {selectedRequest.employee_name} ({selectedRequest.emp_code})
                            </p>
                            <p>
                                <strong>Attendance Date:</strong>{" "}
                                {dayjs(selectedRequest.date).format("DD/MM/YYYY")}
                            </p>
                            <p>
                                <strong>Current Status:</strong>{" "}
                                <Tag color={getStatusColor(selectedRequest.status)}>
                                    {selectedRequest.status}
                                </Tag>
                            </p>
                            <p>
                                <strong>Requested Status:</strong>{" "}
                                <Tag color={getStatusColor(selectedRequest.pending_status_change)}>
                                    {selectedRequest.pending_status_change}
                                </Tag>
                            </p>
                            <p>
                                <strong>Request Remarks:</strong>{" "}
                                {selectedRequest.status_change_remarks || "-"}
                            </p>
                            <p>
                                <strong>Requested By:</strong>{" "}
                                {selectedRequest.requested_by_name}
                            </p>
                        </div>

                        <div>
                            <label
                                style={{
                                    display: "block",
                                    marginBottom: "8px",
                                    fontWeight: "500",
                                }}
                            >
                                {actionType === "approve" ? "Approval Reason:" : "Rejection Reason:"}{" "}
                                <span style={{ color: "red" }}>*</span>
                            </label>
                            <TextArea
                                rows={4}
                                placeholder={
                                    actionType === "approve"
                                        ? "Enter reason for approval..."
                                        : "Enter reason for rejection..."
                                }
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                maxLength={500}
                                showCount
                            />
                        </div>
                    </div>
                )}
            </Modal>
        </section>
    );
}